import React, { useState, useEffect, useRef } from 'react';
import { Inbox, Send, Search, X, CheckCircle, AlertCircle, User, Clock, MessageSquare, UserPlus, Shield, Hash, Calendar, Eye, Filter, Trash2 } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './AdminInboxPanel.css';

const AdminInboxPanel = ({ user, socket, t }) => {
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list', 'case', 'newCase'
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, open: 0, closed: 0 });
  const [filters, setFilters] = useState({
    status: 'all',
    assignedTo: 'all'
  });

  // New case form
  const [newCaseForm, setNewCaseForm] = useState({
    searchTerm: '',
    searchResults: [],
    selectedUser: null,
    subject: '',
    message: ''
  });

  // Message form
  const [messageForm, setMessageForm] = useState({
    message: ''
  });

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);
  const [deleteFromView, setDeleteFromView] = useState('list'); // Track where delete was initiated

  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadCases();
  }, [filters]);

  useEffect(() => {
    if (socket) {
      socket.on('newInboxReport', handleNewReport);
      socket.on('inboxMessageReceived', handleMessageReceived);
      socket.on('inboxCaseAssigned', handleCaseAssigned);
      
      return () => {
        socket.off('newInboxReport', handleNewReport);
        socket.off('inboxMessageReceived', handleMessageReceived);
        socket.off('inboxCaseAssigned', handleCaseAssigned);
      };
    }
  }, [socket]);

  useEffect(() => {
    scrollToBottom();
  }, [selectedCase?.messages]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (showDeleteModal) {
      document.body.style.overflow = 'hidden';
      
      // Add ESC key handler
      const handleEscKey = (e) => {
        if (e.key === 'Escape') {
          closeDeleteModal();
        }
      };
      
      document.addEventListener('keydown', handleEscKey);
      
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDeleteModal]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleNewReport = (data) => {
    toast.success(t('newReportFrom', 'New report from {user}: {subject}').replace('{user}', data.reporter.inGameName).replace('{subject}', data.case.subject));
    loadCases();
  };

  const handleMessageReceived = (data) => {
    toast.success(t('newMessageInCase', 'New message in case {caseNumber}').replace('{caseNumber}', data.caseNumber));
    loadCases();
    
    if (selectedCase && selectedCase._id === data.caseId) {
      loadCase(data.caseId);
    }
  };

  const handleCaseAssigned = (data) => {
    toast.info(t('caseAssignedToYou', 'Case {caseNumber} assigned to you').replace('{caseNumber}', data.caseNumber));
    loadCases();
  };

  const loadCases = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.assignedTo !== 'all') params.append('assignedTo', filters.assignedTo);

      const response = await axios.get(`/api/inbox/admin/cases?${params}`);
      setCases(response.data.cases);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Load cases error:', error);
      toast.error(t('failedToLoadInbox', 'Failed to load cases'));
    } finally {
      setLoading(false);
    }
  };

  const loadCase = async (caseId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/inbox/cases/${caseId}`);
      setSelectedCase(response.data);
      setCurrentView('case');
    } catch (error) {
      console.error('Load case error:', error);
      toast.error(t('failedToLoadCase', 'Failed to load case'));
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (searchTerm) => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setNewCaseForm(prev => ({ ...prev, searchResults: [] }));
      return;
    }

    try {
      const response = await axios.get(`/api/inbox/admin/search-users?q=${searchTerm}`);
      setNewCaseForm(prev => ({ ...prev, searchResults: response.data }));
    } catch (error) {
      console.error('Search users error:', error);
    }
  };

  const createNewCase = async () => {
    if (!newCaseForm.selectedUser || !newCaseForm.subject.trim() || !newCaseForm.message.trim()) {
      toast.error(t('pleaseFillAllFields', 'Please fill in all fields'));
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/inbox/admin/create-case', {
        userId: newCaseForm.selectedUser._id,
        subject: newCaseForm.subject.trim(),
        message: newCaseForm.message.trim()
      });

      toast.success(t('caseCreatedSuccessfully', 'Case created successfully'));
      setNewCaseForm({
        searchTerm: '',
        searchResults: [],
        selectedUser: null,
        subject: '',
        message: ''
      });
      setCurrentView('list');
      loadCases();
    } catch (error) {
      // Show specific validation errors if available
      if (error.response?.data?.errors?.length > 0) {
        const errorMsgs = error.response.data.errors.map(e => e.msg).join(', ');
        toast.error(errorMsgs);
      } else {
        const message = error.response?.data?.message || t('failedToCreateCase', 'Failed to create case');
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!messageForm.message.trim()) {
      toast.error(t('pleaseEnterMessage', 'Please enter a message'));
      return;
    }

    try {
      setLoading(true);
      await axios.post(`/api/inbox/cases/${selectedCase._id}/message`, {
        message: messageForm.message.trim()
      });
      
      setMessageForm({ message: '' });
      loadCase(selectedCase._id);
      loadCases();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToSendMessage', 'Failed to send message');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const toggleCaseStatus = async () => {
    const newStatus = selectedCase.status === 'open' ? 'closed' : 'open';
    const reasonPrompt = newStatus === 'closed' ? t('reasonForClosing', 'Reason for closing this case:') : t('reasonForOpening', 'Reason for opening this case:');
    const reason = prompt(reasonPrompt);
    
    if (reason === null) return;

    try {
      setLoading(true);
      await axios.patch(`/api/inbox/admin/cases/${selectedCase._id}/status`, {
        status: newStatus,
        reason: reason.trim()
      });

      toast.success(t('caseStatusSuccessfully', 'Case {status} successfully').replace('{status}', newStatus));
      loadCase(selectedCase._id);
      loadCases();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToUpdateCaseStatus', 'Failed to update case status');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const assignCase = async (assignTo = null) => {
    try {
      setLoading(true);
      await axios.patch(`/api/inbox/admin/cases/${selectedCase._id}/assign`, {
        assignTo
      });

      const message = assignTo ? t('caseAssignedSuccessfully', 'Case assigned successfully') : t('caseUnassignedSuccessfully', 'Case unassigned successfully');
      toast.success(message);
      loadCase(selectedCase._id);
      loadCases();
    } catch (error) {
      const message = error.response?.data?.message || t('failedToAssignCase', 'Failed to assign case');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const deleteCase = async () => {
    if (!caseToDelete || !caseToDelete._id) {
      toast.error(t('noCaseSelectedForDeletion', 'No case selected for deletion'));
      return;
    }

    try {
      setDeleteLoading(true);
      
      const response = await axios.post(`/api/inbox/admin/cases/${caseToDelete._id}/delete`, {
        reason: 'Deleted by admin'
      });
      
      toast.success(t('caseDeletedSuccessfully', 'Case deleted successfully'));
      
      // Reset modal state
      setShowDeleteModal(false);
      setCaseToDelete(null);
      
      // Navigate based on where delete was initiated
      if (deleteFromView === 'case') {
        // If we were viewing the specific case, go back to list
        setCurrentView('list');
        setSelectedCase(null);
      } else {
        // If we were in list view, stay in list view
        setCurrentView('list');
      }
      
      // Always reload cases to refresh the data
      await loadCases();
      
    } catch (error) {
      const message = error.response?.data?.message || t('failedToDeleteCase', 'Failed to delete case');
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteModal = (caseData) => {
    if (!caseData) {
      toast.error(t('noCaseSelected', 'No case selected'));
      return;
    }
    
    setCaseToDelete(caseData);
    setDeleteFromView(currentView); // Remember which view we're deleting from
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setCaseToDelete(null);
    setDeleteFromView('list'); // Reset to default
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString();
  };

  const formatRelativeTime = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}${t('mins', 'm')} ${t('ago', 'ago')}`;
    } else if (diffHours < 24) {
      return `${diffHours}${t('hrs', 'h')} ${t('ago', 'ago')}`;
    } else if (diffDays < 7) {
      return `${diffDays}${t('daysShort', 'd')} ${t('ago', 'ago')}`;
    } else {
      return then.toLocaleDateString();
    }
  };

  const getCaseStatusBadge = (status) => {
    if (status === 'open') {
      return (
        <span className="status-badge status-open">
          <CheckCircle className="w-3 h-3" />
          {t('open', 'Open')}
        </span>
      );
    } else {
      return (
        <span className="status-badge status-closed">
          <X className="w-3 h-3" />
          {t('closed', 'Closed')}
        </span>
      );
    }
  };

  const getRoleIcon = (role) => {
    if (['main_admin', 'manager', 'supervisor', 'approver'].includes(role)) {
      return <Shield className="w-4 h-4" />;
    } else if (role === 'system') {
      return <AlertCircle className="w-4 h-4" />;
    }
    return <User className="w-4 h-4" />;
  };

  const getRoleDisplay = (role) => {
    const roleMap = {
      'main_admin': t('mainAdmin', 'Main Admin'),
      'supervisor': t('supervisor', 'Supervisor'),
      'approver': t('approver', 'Approver'),
      'player': t('player', 'Player'),
      'system': t('system', 'System')
    };
    return roleMap[role] || role;
  };

  const getRoleStyle = (role) => {
    const roleStyleMap = {
      'main_admin': 'role-main-admin',
      'manager': 'role-manager',
      'supervisor': 'role-supervisor',
      'approver': 'role-approver',
      'verified_sponsor': 'role-verified-sponsor',
      'player': 'role-player',
      'system': 'role-system'
    };
    return roleStyleMap[role] || 'role-player';
  };

  // Delete Modal Component - Simplified with view context
  const DeleteModal = () => {
    if (!showDeleteModal || !caseToDelete) return null;

    return (
      <div className="modal-overlay">
        {/* Backdrop */}
        <div className="modal-backdrop" />
        
        {/* Modal Content */}
        <div className="modal-content">
          <div className="modal-header">
            <div className="modal-title-section">
              <div className="modal-icon">
                <Trash2 className="w-6 h-6 text-white" />
              </div>
              <h3 className="modal-title">{t('deleteCase', 'Delete Case')}</h3>
            </div>
            <button
              onClick={closeDeleteModal}
              className="modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="modal-body">
            {/* Show context about current view */}
            <div className="modal-info-card">
              <p className="modal-info-title">
                {deleteFromView === 'case' 
                  ? t('youAreCurrentlyViewing', '📝 You are currently viewing this case')
                  : t('youAreViewingCasesList', '📋 You are viewing the cases list')
                }
              </p>
              <p className="modal-info-subtitle">
                {deleteFromView === 'case' 
                  ? t('afterDeletionReturn', 'After deletion, you will return to the cases list')
                  : t('listWillRefresh', 'The list will refresh after deletion')
                }
              </p>
            </div>

            <div className="modal-case-info">
              <p className="modal-case-title">{t('caseLabel', 'Case:')} {caseToDelete.subject}</p>
              <p className="modal-case-detail">#{caseToDelete.caseNumber}</p>
              <p className="modal-case-detail">{t('userLabel', 'User:')} {caseToDelete.user?.inGameName || t('unknown', 'Unknown')}</p>
              <p className="modal-case-detail">{t('messagesLabel', 'Messages:')} {caseToDelete.messages?.length || 0}</p>
            </div>

            <div className="modal-warning">
              <div className="modal-warning-content">
                <AlertCircle className="modal-warning-icon" />
                <div>
                  <p className="modal-warning-title">{t('warning', 'Warning:')}:</p>
                  <p className="modal-warning-text">
                    {t('deleteCaseText', 'This action cannot be undone. The case and all its messages will be permanently deleted.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="modal-actions">
            <button
              onClick={deleteCase}
              disabled={deleteLoading}
              className="btn-modal-danger"
            >
              {deleteLoading ? (
                <>
                  <div className="loading-spinner"></div>
                  {t('deleting', 'Deleting...')}
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  {t('deleteCase', 'Delete Case')}
                </>
              )}
            </button>
            <button
              onClick={closeDeleteModal}
              disabled={deleteLoading}
              className="btn-modal-cancel"
            >
              {t('cancel', 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (currentView === 'newCase') {
    return (
      <>
        <div className="admin-inbox">
          <div className="inbox-container">
            <div className="inbox-header">
              <div className="inbox-header-content">
                <h3 className="inbox-title">
                  <UserPlus className="w-6 h-6" />
                  {t('createNewCaseForUser', 'Create New Case for User')}
                </h3>
                <button
                  onClick={() => setCurrentView('list')}
                  className="close-button"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="inbox-content">
              <div className="form-group">
                <label className="form-label">
                  {t('selectUser', 'Select User')} <span className="required">*</span>
                </label>
                <div className="search-input-container">
                  <Search className="search-icon" />
                  <input
                    type="text"
                    value={newCaseForm.searchTerm}
                    onChange={(e) => {
                      setNewCaseForm(prev => ({ ...prev, searchTerm: e.target.value }));
                      searchUsers(e.target.value);
                    }}
                    placeholder={t('searchUsersUsername', 'Search users by username, email, or in-game name...')}
                    className="search-input"
                  />
                </div>

                {newCaseForm.searchResults.length > 0 && (
                  <div className="search-results">
                    {newCaseForm.searchResults.map(searchUser => (
                      <div
                        key={searchUser._id}
                        onClick={() => setNewCaseForm(prev => ({
                          ...prev,
                          selectedUser: searchUser,
                          searchResults: [],
                          searchTerm: searchUser.inGameName
                        }))}
                        className="search-result-item"
                      >
                        <p className="search-result-name">{searchUser.inGameName}</p>
                        <p className="search-result-email">{searchUser.email}</p>
                      </div>
                    ))}
                  </div>
                )}

                {newCaseForm.selectedUser && (
                  <div className="selected-user-info">
                    <p className="selected-user-text">
                      {t('selected', 'Selected:')} {newCaseForm.selectedUser.inGameName} ({newCaseForm.selectedUser.email})
                    </p>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('subject', 'Subject')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={newCaseForm.subject}
                  onChange={(e) => setNewCaseForm(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder={t('caseSubject', 'Case subject...')}
                  className="text-input"
                  maxLength={200}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  {t('initialMessage', 'Initial Message')} <span className="required">*</span>
                </label>
                <textarea
                  value={newCaseForm.message}
                  onChange={(e) => setNewCaseForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={t('initialMessageToUser', 'Initial message to the user...')}
                  className="text-input textarea"
                  rows={4}
                  maxLength={1000}
                />
              </div>

              <div className="form-actions">
                <button
                  onClick={createNewCase}
                  disabled={loading || !newCaseForm.selectedUser || !newCaseForm.subject.trim() || !newCaseForm.message.trim()}
                  className="btn-primary"
                >
                  {loading ? t('creating', 'Creating...') : t('createCaseBtn', 'Create Case')}
                </button>
                <button
                  onClick={() => setCurrentView('list')}
                  className="btn-secondary"
                >
                  {t('cancel', 'Cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <DeleteModal />
      </>
    );
  }

  if (currentView === 'case' && selectedCase) {
    return (
      <>
        <div className="case-container">
          <div className="case-panel">
            <div className="case-header">
              <div className="case-header-content">
                <div className="case-title-section">
                  <div className="case-title-row">
                    <MessageSquare className="w-6 h-6 text-orange-400" />
                    <h3 className="case-title">{selectedCase.subject}</h3>
                    {getCaseStatusBadge(selectedCase.status)}
                  </div>
                  
                  <div className="case-info-grid">
                    <div className="case-info-item">
                      <Hash className="case-info-icon" />
                      <span>{t('caseNumber', 'Case')} {selectedCase.caseNumber}</span>
                    </div>
                    <div className="case-info-item">
                      <User className="case-info-icon" />
                      <span>{t('userLabel', 'User:')} {selectedCase.user.inGameName}</span>
                    </div>
                    <div className="case-info-item">
                      <Calendar className="case-info-icon" />
                      <span>{t('createdLabel', 'Created:')} {formatRelativeTime(selectedCase.createdAt)}</span>
                    </div>
                    <div className="case-info-item">
                      <Clock className="case-info-icon" />
                      <span>{t('updatedLabel', 'Updated:')} {formatRelativeTime(selectedCase.lastActivity)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="case-actions">
                  <button
                    onClick={toggleCaseStatus}
                    disabled={loading}
                    className={`btn-action ${selectedCase.status === 'open' ? 'btn-close-case' : 'btn-open-case'}`}
                  >
                    {selectedCase.status === 'open' ? t('closeCase', 'Close Case') : t('openCase', 'Open Case')}
                  </button>
                  
                  {!selectedCase.assignedTo || selectedCase.assignedTo._id !== user._id ? (
                    <button
                      onClick={() => assignCase(user._id)}
                      disabled={loading}
                      className="btn-action btn-assign"
                    >
                      {t('assignToMe', 'Assign to Me')}
                    </button>
                  ) : (
                    <button
                      onClick={() => assignCase(null)}
                      disabled={loading}
                      className="btn-action btn-unassign"
                    >
                      {t('unassignCase', 'Unassign')}
                    </button>
                  )}

                  {['main_admin', 'manager', 'supervisor'].includes(user.role) && (
                    <button
                      onClick={() => openDeleteModal(selectedCase)}
                      disabled={loading}
                      className="btn-action btn-delete"
                      title={t('deleteCase', 'Delete Case')}
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('deleteCase', 'Delete Case')}
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setCurrentView('list');
                      setSelectedCase(null);
                    }}
                    className="close-button"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {selectedCase.assignedTo && (
                <div className="assigned-info">
                  <p className="assigned-text">
                    {t('assignedTo', 'Assigned to:')} {selectedCase.assignedTo.inGameName} ({selectedCase.assignedTo.role})
                  </p>
                </div>
              )}
            </div>

            <div className="messages-container">
              {selectedCase.messages.map((msg, index) => {
                const isFromUser = msg.from._id === selectedCase.user._id;
                const isSystem = msg.isSystemMessage;
                
                return (
                  <div
                    key={index}
                    className={`message-wrapper ${isFromUser ? 'from-user' : 'from-admin'}`}
                  >
                    <div className={`message-content ${isFromUser ? 'from-user' : 'from-admin'}`}>
                      <div className={`message-header ${isFromUser ? 'from-user' : 'from-admin'}`}>
                        <div className={`role-badge ${getRoleStyle(msg.fromRole)}`}>
                          {getRoleIcon(msg.fromRole)}
                          <span>
                            {isSystem ? t('system', 'System') : 
                             isFromUser ? selectedCase.user.inGameName :
                             msg.from.inGameName || getRoleDisplay(msg.fromRole)}
                          </span>
                        </div>
                        <span className="message-timestamp">
                          {formatRelativeTime(msg.timestamp)}
                        </span>
                      </div>
                      
                      <div
                        className={`message-bubble ${
                          isSystem
                            ? 'system'
                            : isFromUser
                            ? 'from-user'
                            : 'from-admin'
                        }`}
                      >
                        <p className="message-text">
                          {msg.message}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="message-input-area">
              <div className="message-input-container">
                <textarea
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ message: e.target.value })}
                  placeholder={t('typeYourResponse', 'Type your response...')}
                  className="message-textarea"
                  rows={2}
                  maxLength={1000}
                  disabled={loading}
                />
                <button
                  onClick={sendMessage}
                  disabled={loading || !messageForm.message.trim()}
                  className="btn-send"
                >
                  <Send className="w-4 h-4" />
                  {loading ? t('sending', 'Sending...') : t('send', 'Send')}
                </button>
              </div>
              <p className="character-count">{t('characterCount', '{count}/1000').replace('{count}', messageForm.message.length)}</p>
            </div>
          </div>
        </div>
        <DeleteModal />
      </>
    );
  }

  return (
    <>
      <div className="overview-container">
        <div className="stats-panel">
          <div className="stats-header">
            <h3 className="stats-title">
              <Inbox className="w-6 h-6 text-orange-400" />
              {t('supportInboxManagement', 'Support Inbox Management')}
            </h3>
            <button
              onClick={() => setCurrentView('newCase')}
              className="btn-create-case"
            >
              <UserPlus className="w-4 h-4" />
              {t('createCase', 'Create Case')}
            </button>
          </div>

          <div className="stats-grid">
            <div className="stat-card total">
              <div className="stat-content">
                <div className="stat-icon total">
                  <Inbox className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="stat-number">{summary.total}</p>
                  <p className="stat-label total">{t('totalCases', 'Total Cases')}</p>
                </div>
              </div>
            </div>
            
            <div className="stat-card open">
              <div className="stat-content">
                <div className="stat-icon open">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="stat-number">{summary.open}</p>
                  <p className="stat-label open">{t('openCases', 'Open Cases')}</p>
                </div>
              </div>
            </div>
            
            <div className="stat-card closed">
              <div className="stat-content">
                <div className="stat-icon closed">
                  <X className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="stat-number">{summary.closed}</p>
                  <p className="stat-label closed">{t('closedCases', 'Closed Cases')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="filters-container">
            <div className="filter-group">
              <Filter className="w-4 h-4 text-slate-400" />
              <label className="filter-label">{t('status', 'Status')}:</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="filter-select"
              >
                <option value="all">{t('allStatus', 'All Status')}</option>
                <option value="open">{t('open', 'Open')}</option>
                <option value="closed">{t('closed', 'Closed')}</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">{t('assignment', 'Assignment')}:</label>
              <select
                value={filters.assignedTo}
                onChange={(e) => setFilters(prev => ({ ...prev, assignedTo: e.target.value }))}
                className="filter-select"
              >
                <option value="all">{t('allAssignments', 'All Assignments')}</option>
                <option value="unassigned">{t('unassigned', 'Unassigned')}</option>
                <option value={user._id}>{t('assignedToMe', 'Assigned to Me')}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="cases-list-panel">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">{t('loadingCases', 'Loading cases...')}</p>
            </div>
          ) : cases.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <Inbox className="w-12 h-12 text-slate-400" />
              </div>
              <h4 className="empty-state-title">{t('noCasesFound', 'No cases found')}</h4>
              <p className="empty-state-text">
                {t('adjustFiltersOrCreateCase', 'Adjust filters or create a new case for a user')}
              </p>
            </div>
          ) : (
            <div className="cases-list">
              {cases.map((caseData) => {
                const lastMessage = caseData.messages?.[caseData.messages.length - 1];
                
                return (
                  <div
                    key={caseData._id}
                    onClick={() => loadCase(caseData._id)}
                    className={`case-item ${caseData.status}`}
                  >
                    <div className="case-item-content">
                      <div className="case-item-header">
                        <div className="case-item-title-section">
                          <div className="case-item-title-row">
                            <h4 className="case-item-title">
                              {caseData.subject}
                            </h4>
                            {getCaseStatusBadge(caseData.status)}
                          </div>
                          
                          <div className="case-item-info">
                            <div className="case-item-info-row">
                              <div className="case-item-info-item">
                                <Hash className="case-info-icon" />
                                <span className="case-item-user">{t('caseNumber', 'Case')} {caseData.caseNumber}</span>
                              </div>
                            </div>
                            
                            <div className="case-item-info-row">
                              <div className="case-item-info-item">
                                <User className="case-info-icon" />
                                <span>{t('userLabel', 'User:')} <span className="case-item-user">{caseData.user?.inGameName || t('unknown', 'Unknown')}</span></span>
                              </div>
                              <div className="case-item-info-item">
                                <Calendar className="case-info-icon" />
                                <span>{t('createdLabel', 'Created:')} {formatDate(caseData.createdAt)}</span>
                              </div>
                            </div>
                            
                            <div className="case-item-info-row">
                              <div className="case-item-info-item">
                                <Clock className="case-info-icon" />
                                <span>{t('updatedLabel', 'Updated:')} {formatRelativeTime(caseData.lastActivity)}</span>
                              </div>
                              <div className="case-item-info-item">
                                <MessageSquare className="case-info-icon" />
                                <span>{caseData.messages?.length || 0} {t('messages', 'messages')}</span>
                              </div>
                            </div>
                          </div>
                          
                          {caseData.assignedTo && (
                            <div className="assigned-info">
                              <p className="assigned-text">
                                {t('assignedTo', 'Assigned to:')} <span className="case-item-user">{caseData.assignedTo.inGameName}</span>
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="case-item-view">
                          <Eye className="case-item-view-icon" />
                          <span className="case-item-view-text">{t('viewCase', 'View')}</span>
                        </div>
                      </div>

                      {lastMessage && (
                        <div className="case-last-message">
                          <div className="case-last-message-header">
                            <div className={`case-last-message-role ${getRoleStyle(lastMessage.fromRole)}`}>
                              {getRoleIcon(lastMessage.fromRole)}
                              <span>
                                {lastMessage.isSystemMessage ? t('system', 'System') : 
                                 lastMessage.from?._id === caseData.user?._id ? caseData.user?.inGameName :
                                 getRoleDisplay(lastMessage.fromRole)}
                              </span>
                            </div>
                            <span className="case-last-message-timestamp">
                              {formatRelativeTime(lastMessage.timestamp)}
                            </span>
                          </div>
                          <p className="case-last-message-text">
                            {lastMessage.message}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <DeleteModal />
    </>
  );
};

export default AdminInboxPanel;